<template>
  <div class="edit-module">
    <v-skeleton-loader v-if="loading" type="heading, list-item-three-line, image" class="ma-3" />

    <v-alert v-else-if="loadError" type="error" text class="ma-3">
      {{ loadError }}
    </v-alert>

    <v-form v-else ref="form" v-model="valid" lazy-validation>
      <v-container fluid class="pa-2 pa-sm-3">
        <v-row dense align="start">
          <v-col cols="12" md="4" lg="3">
            <v-card outlined class="edit-module__identity">
              <v-card-title class="py-2 px-3 text-subtitle-1 font-weight-medium">
                <v-icon left color="indigo">mdi-puzzle-edit-outline</v-icon>
                Identité du module
              </v-card-title>
              <v-divider />

              <v-card-text class="pa-3">
                <v-select
                  v-model="module.type"
                  :items="listModules"
                  :disabled="moduleLocked"
                  :rules="requiredRules"
                  label="Type de module"
                  prepend-inner-icon="mdi-view-module-outline"
                  dense outlined hide-details="auto"
                  class="mb-3"
                  @change="changeModuleType"
                />

                <v-autocomplete
                  v-model="module.table"
                  :items="itemTableListe"
                  :disabled="moduleLocked"
                  :rules="requiredRules"
                  label="Table principale"
                  prepend-inner-icon="mdi-table"
                  dense outlined clearable hide-details="auto"
                  class="mb-3"
                />

                <v-text-field
                  v-model="module.name"
                  label="Nom du module"
                  prepend-inner-icon="mdi-form-textbox"
                  dense outlined hide-details="auto"
                  class="mb-3"
                />

                <v-textarea
                  v-model="module.description"
                  label="Description"
                  prepend-inner-icon="mdi-text-long"
                  rows="3" auto-grow dense outlined hide-details="auto"
                />
              </v-card-text>

              <v-divider v-if="module.type" />
              <v-card-actions v-if="module.type" class="d-block pa-3">
                <v-btn block outlined color="indigo" @click="openSaveTemplate = true">
                  <v-icon left>mdi-content-save-cog-outline</v-icon>
                  Modèles de configuration
                </v-btn>
                <v-btn block outlined color="blue-grey" class="mt-2 ml-0" @click="showModuleArray = true">
                  <v-icon left>mdi-code-json</v-icon>
                  Afficher l’array du module
                </v-btn>
                <v-btn
                  block
                  color="primary"
                  :disabled="!valid || saving"
                  :loading="saving"
                  class="mt-2 ml-0"
                  @click="validate"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Enregistrer
                </v-btn>
                <v-btn
                  block
                  color="success"
                  :disabled="!valid || saving"
                  :loading="saving"
                  class="mt-2 ml-0"
                  @click="validate(true)"
                >
                  <v-icon left>mdi-content-save-move-outline</v-icon>
                  Enregistrer et fermer
                </v-btn>
              </v-card-actions>
            </v-card>

            <loadSaveTemplate
              v-if="module.type"
              :page="null"
              :openModal="openSaveTemplate"
              type="moduleparams"
              :templateToSave="templateToSave"
              @closeLST="openSaveTemplate = false"
              @loadTemplate="loadTemplate"
            />

            <v-dialog v-model="showModuleArray" max-width="1100" scrollable>
              <v-card>
                <v-card-title class="py-2 px-3 blue-grey lighten-5">
                  <v-icon left color="blue-grey darken-2">mdi-code-json</v-icon>
                  <span class="text-subtitle-1 font-weight-medium">Array du module</span>
                  <v-spacer />
                  <v-btn icon @click="showModuleArray = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-0">
                  <pre class="edit-module__json">{{ moduleArrayJson }}</pre>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-col>

          <v-col cols="12" md="8" lg="9">
            <v-card outlined>
              <v-tabs v-model="tab" background-color="indigo lighten-5" color="indigo" show-arrows>
                <v-tab>
                  <v-icon left small>mdi-tune-variant</v-icon>
                  Configuration par défaut
                </v-tab>
                <v-tab v-if="moduleLocked">
                  <v-icon left small>mdi-domain</v-icon>
                  Par entreprise
                </v-tab>
              </v-tabs>
              <v-divider />

              <v-tabs-items v-model="tab">
                <v-tab-item>
                  <div class="edit-module__configuration">
                    <component
                      v-if="canDisplayAdmin"
                      :is="adminComponent"
                      :key="'module-admin-' + reloadComp"
                      :module="module"
                      :modalKey="editorContextKey"
                      :page="null"
                      :itemTableListe="itemTableListe"
                      :buildingParameters="dependencies.listParameters"
                      @recepVal="updateDisplays"
                    />

                    <v-alert v-else-if="module.type && !adminComponent" type="error" text class="ma-3">
                      Le composant d’administration du module « {{ module.type }} » est introuvable.
                    </v-alert>

                    <v-alert v-else type="info" text class="ma-3">
                      <div class="d-flex align-center">
                        <v-icon color="info" class="mr-3">mdi-information-outline</v-icon>
                        <span>Sélectionnez un type de module et sa table principale pour afficher sa configuration.</span>
                      </div>
                    </v-alert>
                  </div>
                </v-tab-item>

                <v-tab-item v-if="moduleLocked">
                  <v-list v-if="listEntreprises.length" two-line class="py-0">
                    <template v-for="(entreprise, index) in listEntreprises">
                      <v-list-item :key="entreprise.id">
                        <v-list-item-avatar color="indigo lighten-5">
                          <v-icon color="indigo">mdi-domain</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ entreprise.nom }}</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ hasVariant(entreprise.id) ? 'Configuration personnalisée' : 'Utilise la configuration par défaut' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn small outlined color="indigo" @click="openVariant(entreprise)">
                            <v-icon small left>{{ hasVariant(entreprise.id) ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                            {{ hasVariant(entreprise.id) ? 'Modifier' : 'Créer' }}
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index < listEntreprises.length - 1" :key="'divider-' + entreprise.id" />
                    </template>
                  </v-list>

                  <v-alert v-else type="info" text class="ma-3">
                    Aucune entreprise n’est disponible pour créer une variante.
                  </v-alert>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="mt-3">
          <v-card-actions class="justify-end pa-3">
            <v-btn color="success" :disabled="!valid || saving" :loading="saving" @click="validate(true)">
              <v-icon left>mdi-content-save-move-outline</v-icon>
              Enregistrer et fermer
            </v-btn>
            <v-btn color="primary" :disabled="!valid || saving" :loading="saving" class="px-6" @click="validate">
              <v-icon left>mdi-content-save</v-icon>
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-form>
  </div>
</template>

<script>
import { moduleEditorLocation, openModuleVariant } from './navigation'

export default {
  name: 'EditModuleMain',

  props: {
    moduleId: { type: [String, Number], default: 'new' },
    initialType: { type: String, default: '' },
    initialTable: { type: String, default: '' },
    contextKey: { type: String, default: '' },
    embedded: { type: Boolean, default: false },
    standalone: { type: Boolean, default: false },
  },

  components: {
    loadSaveTemplate: () => import('@/components/main/templates/page/Edit/loadAndSaveTemplate.vue'),
  },

  data() {
    return {
      module: null,
      dependencies: { listParams: {}, listParameters: {} },
      itemTableListe: [],
      listEntreprises: [],
      currentModuleId: this.moduleId,
      internalContextKey: `edit-module-${this._uid}`,
      valid: true,
      loading: true,
      saving: false,
      loadError: '',
      tab: 0,
      openSaveTemplate: false,
      showModuleArray: false,
      reloadComp: 0,
      requiredRules: [value => Boolean(value) || 'Ce champ est obligatoire'],
    }
  },

  computed: {
    listModules() {
      return Object.values(this.$modules)
        .filter(plugin => plugin && plugin.name && plugin.components && plugin.components.admin)
        .map(plugin => ({ text: plugin.label || plugin.name, value: plugin.name }))
        .sort((left, right) => left.text.localeCompare(right.text, 'fr'))
    },

    moduleLocked() {
      return Boolean(this.module && this.module.id)
    },

    adminComponent() {
      const plugin = this.module && this.module.type ? this.$modules[this.module.type] : null
      return plugin && plugin.components ? plugin.components.admin : null
    },

    canDisplayAdmin() {
      return Boolean(this.adminComponent && this.module.type && this.module.table)
    },

    editorContextKey() {
      return this.contextKey || this.internalContextKey
    },

    templateToSave() {
      return this.module && this.module.displays ? this.module.displays : null
    },

    moduleArrayJson() {
      return JSON.stringify(this.module || {}, null, 2)
    },
  },

  methods: {
    clone(value) {
      return JSON.parse(JSON.stringify(value))
    },

    async loadInitialData() {
      this.loading = true
      this.loadError = ''

      try {
        const [moduleResponse, entreprisesResponse] = await Promise.all([
          this.API({
            type: 'post',
            url: 'engine/module/edit',
            params: { filter: { id: this.currentModuleId, variant: 0 } },
          }),
          this.API({ type: 'post', url: 'engine/entreprises/list', params: {} }),
        ])

        this.dependencies = moduleResponse.data.dependencies || { listParams: {}, listParameters: {} }
        this.itemTableListe = (this.dependencies.listTables || []).map(table => ({ text: table, value: table }))
        this.listEntreprises = entreprisesResponse.data || []
        this.module = this.clone(moduleResponse.data.module)

        if (!this.module.id) {
          if (this.initialType) {
            this.module.type = this.initialType
            this.applyDefaultParams(this.initialType)
          }
          if (this.initialTable) this.module.table = this.initialTable
        }
      } catch (error) {
        this.loadError = 'Impossible de charger la configuration du module.'
      } finally {
        this.loading = false
      }
    },

    applyDefaultParams(type) {
      const defaults = this.dependencies.listParams && this.dependencies.listParams[type]
      if (!defaults || !this.module.displays) return
      this.module.displays.params = this.clone(defaults)
    },

    changeModuleType(type) {
      this.applyDefaultParams(type)
      this.reloadComp++
    },

    updateDisplays(displays) {
      this.$set(this.module, 'displays', displays)
    },

    loadTemplate(displays) {
      this.updateDisplays(this.clone(displays))
      this.reloadComp++
      this.openSaveTemplate = false
    },

    hasVariant(entrepriseId) {
      return (this.module.listvariant || []).some(variant => Number(variant) === Number(entrepriseId))
    },

    openVariant(entreprise) {
      openModuleVariant({
        moduleId: this.module.id,
        variant: entreprise.id,
        contextKey: this.editorContextKey,
      })
    },

    async validate(closeAfterSave = false) {
      if (!this.$refs.form.validate()) return

      this.saving = true
      this.$store.dispatch('getoverlay', true)
      try {
        const response = await this.API({
          type: 'put',
          url: 'engine/module/edit',
          params: { id: this.currentModuleId, fields: this.module, variant: 0 },
        })

        const wasNewModule = String(this.currentModuleId) === 'new'
        if (wasNewModule) {
          this.$set(this.module, 'id', response.data.module.id)
          this.$set(this.module, 'listvariant', response.data.module.listvariant || [0])
        }
        this.currentModuleId = this.module.id
        this.$store.dispatch('addReloadModule', { modules: Number(this.module.id) })
        this.$emit('saved', this.module)

        if (closeAfterSave === true) {
          if (this.embedded) this.$emit('close')
          else if (this.standalone) this.$router.back()
          return
        }

        if (wasNewModule && this.standalone && this.$route.name === 'engine-module-edit') {
          this.$router.replace(moduleEditorLocation({ id: this.module.id }))
        }
      } finally {
        this.saving = false
        this.$store.dispatch('getoverlay', false)
      }
    },
  },

  mounted() {
    this.loadInitialData()
  },
}
</script>

<style scoped>
.edit-module {
  width: 100%;
}

.edit-module__configuration {
  min-height: 180px;
  padding: 4px;
}

.edit-module__json {
  min-height: 240px;
  max-height: 70vh;
  margin: 0;
  padding: 16px;
  overflow: auto;
  color: #eceff1;
  background: #263238;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (min-width: 960px) {
  .edit-module__identity {
    position: sticky;
    top: 8px;
  }
}

@media (max-width: 599px) {
  .edit-module ::v-deep .v-tab {
    min-width: auto;
    padding: 0 12px;
    font-size: 0.75rem;
  }

  .edit-module ::v-deep .v-list-item {
    padding-left: 8px;
    padding-right: 8px;
  }

  .edit-module ::v-deep .v-list-item__avatar {
    margin-right: 8px;
  }

  .edit-module ::v-deep .v-list-item__action .v-btn {
    min-width: 36px;
    padding: 0 8px;
  }
}
</style>
